/**
 * ImportStatusCard - shared card wrapper for the four import-status
 * panels on AdminImportStatusPage (Phase 12.5).
 *
 * Each card shows: title, enabled / not-configured badge, a slot for
 * body content (counts, progress bar, last-sync timestamp, etc.), and
 * a "not configured" note (rendered only when ``enabled === false``).
 */
import type { ReactNode } from "react";

import { Badge, Card } from "@/components/ui";

export interface ImportStatusCardProps {
  title: string;
  enabled: boolean;
  /** Note rendered when ``enabled === false``. */
  notConfiguredNote?: string;
  /** Optional override of the enabled-state badge text. */
  enabledBadgeLabel?: string;
  /** Optional override of the disabled-state badge text. */
  disabledBadgeLabel?: string;
  children: ReactNode;
}

export function ImportStatusCard({
  title,
  enabled,
  notConfiguredNote,
  enabledBadgeLabel = "Enabled",
  disabledBadgeLabel = "Not configured",
  children,
}: ImportStatusCardProps): JSX.Element {
  return (
    <Card padding="md">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-base font-semibold text-neutral-900">{title}</h3>
        {enabled ? (
          <Badge variant="success">{enabledBadgeLabel}</Badge>
        ) : (
          <Badge variant="default">{disabledBadgeLabel}</Badge>
        )}
      </div>
      <div className="mt-3">{children}</div>
      {!enabled && notConfiguredNote && (
        <p className="mt-3 rounded-md bg-neutral-50 p-2 text-xs text-neutral-600">
          {notConfiguredNote}
        </p>
      )}
    </Card>
  );
}

export default ImportStatusCard;
